/* eslint-disable no-unused-vars */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { saveApplication, saveChatResponse } from '../../../services/questionService';
import { getSummaryGenerator } from '../../../services/getSummary';
import LoadingScreen from './loadingScreen';
import TermsAndConditions from './termsAndConditions';
import {
  ContentBox,
  QuestionBox,
  TextInput,
  ContinueButton,
  PropertyList,
  PropertyItem,
  PropertyLabel,
  PropertyValue,
  Main,
  SectionLeft,
  SectionRight,
  ContentBoxRight,
  ActionLink,
  Wrapper,
  // ProgressContainer,
  // ProgressBarWrapper,
  // ProgressBarFill,
  // ProgressInfo,
  // ProgressText,
  RadioGroup,
  RadioOption,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalText,
  ModalButton,
} from './generateQuestion.style';
import { ErrorMessageComponent } from '../../../components/uielements/error';
import {
  cacheAnswer,
  cacheSummary,
  clearCache,
  clearSummaryCache,
  getCache,
  getCacheOverlaysScreenshots,
  getSummaryCache,
} from '../../../helper/cacheHelper';
import { clearItem, getItem, setItem } from '../../../helper/localStorageHelper';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { IntlMessages } from '../../../helper/langHelper';
import { FormattedMessage, useIntl } from 'react-intl';
import MapWithOverlays from '../mapView/mapWithOverlays';
import { getCriterias, getQuestion } from '../../../services/assessment';
import { useGeneralStore } from '../../../store/generalStore';
import useMediaQuery from '../../../hooks/useMediaQuery';

// Constants for phase management
const PHASES = {
  INIT: 'INIT',
  QUESTION: 'QUESTION',
  TERMS: 'TERMS',
  COMPLETED: 'COMPLETED',
  NOT_SUPPORTED: 'NOT_SUPPORTED',
};

const QuestionComponent = () => {
  const [answer, setAnswer] = useState('');
  const [proposal, setProposal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [loadingQuestionId, setLoadingQuestionId] = useState(null);
  const [error, setError] = useState('');
  const [isEndQuestion, setIsEndQuestion] = useState(false);
  const [isProcedureCompleted, setIsProcedureCompleted] = useState(false);
  const [showNotSupportedModal, setShowNotSupportedModal] = useState(false);

  // breakPoints
  const isMobile = useMediaQuery('(max-width: 480px)');

  // LIFO question queue — master list of question codes from getCriterias
  const [questionCodeStack, setQuestionCodeStack] = useState([]);

  // Current active question state
  const [currentQuestion, setCurrentQuestion] = useState(null);
  // currentQuestion shape: { questionType, question, questionCode, options, nextQuestionCode, clusterData }

  // Validation error from API
  const [validationError, setValidationError] = useState('');

  // Track all submitted answers for final payload
  const [allAnswers, setAllAnswers] = useState([]);

  // Current phase
  const [currentPhase, setCurrentPhase] = useState(PHASES.INIT);

  // Progress tracking
  const [totalCodes, setTotalCodes] = useState(0);
  const [completedCodes, setCompletedCodes] = useState(0);

  const cacheOverlayScreenshots = getCacheOverlaysScreenshots();

  const { folderName, clearFlow } = useGeneralStore();

  const location = useLocation();
  const navigate = useNavigate();
  const selectedProcedure = location.state?.selectedProcedure || '';
  const propertyAddress = location.state?.searchAddress?.address || '';
  const overlays = location.state?.overlays || '';
  const fullName = location.state?.fullName || '';
  const email = location.state?.email || '';
  const phone = location.state?.phone || '';
  const description = location.state?.inputValue || '';
  const screenshotIdFromUrl = location.state?.screenshotId || '';
  const zoneDetails = location.state?.zoneDetailsData || {};
  const applicationIdForConvertToFinal = location.state?.applicationId || null;

  const procedurePermit = getItem('procedurePermit') || {};
  const qaCoversation = getCache();
  const summarayCache = getSummaryCache();
  const screenshotId = getItem('screenshotId');
  const intl = useIntl();

  // ─── Init ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    const isTerminateExists = qaCoversation.some(
      (entry) => entry?.question === 'TERMINATE' && entry?.answer === 'TERMINATE'
    );

    if (!isTerminateExists) {
      clearCache();
      clearSummaryCache();
    }

    fetchInitialData();
  }, []);

  // ─── Utilities ──────────────────────────────────────────────────────────────

  const handleError = useCallback((errorMessage, fallbackKey = 'generic.error') => {
    setError(<IntlMessages id={fallbackKey} defaultMessage={errorMessage} />);
    setLoading(false);
  }, []);

  const resetFormState = useCallback(() => {
    setAnswer('');
    setError('');
    setValidationError('');
  }, []);

  // ─── Initial data fetch ─────────────────────────────────────────────────────

  const fetchInitialData = async () => {
    setLoading(true);

    const isTerminateExists = qaCoversation.some(
      (entry) => entry?.question === 'TERMINATE' && entry?.answer === 'TERMINATE'
    );

    if (isTerminateExists) {
      setIsProcedureCompleted(true);
      setLoading(false);
      return;
    }

    try {
      const criteriaPayload = {
        regionId: procedurePermit?.results[0]?.regionId,
        precinctId: procedurePermit?.results[0]?.precinctId,
        procedureId: procedurePermit?.results[0]?.procedureId,
      };

      const criteriaResponse = await getCriterias(criteriaPayload);

      // criteriaResponse is expected to have an array of question codes
      // e.g. { questionCodes: ["6AD4870ADDBD", "XXXX", ...] }
      const codes = criteriaResponse || [];

      if (!codes || codes.length === 0) {
        setShowNotSupportedModal(true);
        setCurrentPhase(PHASES.NOT_SUPPORTED);
        setLoading(false);
        return;
      }

      setTotalCodes(codes.length);
      // Store all codes; we'll pop from the end (LIFO)
      setQuestionCodeStack(codes);

      // Kick off the first question using the last code (LIFO)
      const lastCode = codes[codes.length - 1];
      const remainingStack = codes.slice(0, codes.length - 1);
      setQuestionCodeStack(remainingStack);

      await startFlowWithCode(lastCode, remainingStack);
    } catch (err) {
      console.error('Error fetching criteria:', err);
      handleError('Error fetching criteria. Please try again.', 'criteria.fetch.error');
    } finally {
      setLoading(false);
    }
  };

  // ─── LIFO Flow: start a new flow with a question code (no response) ─────────

  /**
   * Calls getQuestion with only questionCode (no response) to start a new sub-flow.
   * stackSnapshot is passed in to avoid stale closure issues.
   */
  const startFlowWithCode = async (questionCode, stackSnapshot) => {
    setLoading(true);
    resetFormState();

    try {
      const response = await getQuestion({ questionCode });

      await processApiResponse(response, stackSnapshot);
    } catch (err) {
      console.error('Error starting flow with code:', questionCode, err);
      handleError('Error fetching question. Please try again.', 'question.fetch.error');
    } finally {
      setLoading(false);
    }
  };

  // ─── Submit current answer ──────────────────────────────────────────────────

  const handleSubmit = async () => {
    setValidationError('');
    setError('');

    if (!answer || (typeof answer === 'string' && answer.trim() === '')) {
      setError(<IntlMessages id="answer.required" defaultMessage="Please provide an answer." />);
      return;
    }

    setLoading(true);

    try {
      const response = await getQuestion({
        questionCode: currentQuestion.questionCode,
        response: answer,
      });

      await processApiResponse(response, questionCodeStack);
    } catch (err) {
      console.error('Error submitting answer:', err);
      handleError('Error submitting answer. Please try again.', 'submit.error');
    } finally {
      setLoading(false);
    }
  };

  // ─── Central response processor ─────────────────────────────────────────────

  /**
   * Handles any getQuestion() API response.
   * stackSnapshot: current in-memory code stack at the moment of the call.
   */
  const processApiResponse = async (response, stackSnapshot) => {
    const { status, data } = response;

    if (status === 'DATA') {
      // Append any nextQuestionCode values to the stack (they go to the end;
      // LIFO pops from the end so they become higher priority than older codes)
      let updatedStack = [...stackSnapshot];
      if (data.nextQuestionCode && data.nextQuestionCode.length > 0) {
        updatedStack = [...updatedStack, ...data.nextQuestionCode];
        setQuestionCodeStack(updatedStack);
      } else {
        setQuestionCodeStack(updatedStack);
      }

      // Cache the answer for the previous question if there was one
      if (currentQuestion && answer) {
        const isSingleProceedOption =
          currentQuestion.questionType === 'OPTION' &&
          currentQuestion.options?.length === 1 &&
          currentQuestion.options[0] === 'Proceed!';

        if (!isSingleProceedOption) {
          // Generate summary for meaningful answers
          await generateSummaryForAnswer(
            currentQuestion.question,
            answer,
            currentQuestion.clusterData?.name || currentQuestion.questionCode
          );
        }

        cacheAnswer(currentQuestion.question, answer);
        setAllAnswers((prev) => [
          ...prev,
          {
            question: currentQuestion.question,
            answer: answer,
            questionCode: currentQuestion.questionCode,
            clusterName: currentQuestion.clusterData?.name,
            timestamp: new Date().toISOString(),
          },
        ]);
      }

      // Show new question
      setCurrentQuestion(data);
      setCurrentPhase(PHASES.QUESTION);
      resetFormState();
    } else if (status === 'VALIDATION') {
      // Keep the same question, show validation error, preserve user's typed answer
      setValidationError(
        data?.message || 'Invalid response. Please check your answer and try again.'
      );
      // Do NOT reset answer so user can see and correct what they typed
    } else if (status === 'TERMINATE') {
      // Cache the final answer before terminating
      if (currentQuestion && answer) {
        const isSingleProceedOption =
          currentQuestion.questionType === 'OPTION' &&
          currentQuestion.options?.length === 1 &&
          currentQuestion.options[0] === 'Proceed!';

        if (!isSingleProceedOption) {
          await generateSummaryForAnswer(
            currentQuestion.question,
            answer,
            currentQuestion.clusterData?.name || currentQuestion.questionCode
          );
        }

        cacheAnswer(currentQuestion.question, answer);
        setAllAnswers((prev) => [
          ...prev,
          {
            question: currentQuestion.question,
            answer: answer,
            questionCode: currentQuestion.questionCode,
            clusterName: currentQuestion.clusterData?.name,
            timestamp: new Date().toISOString(),
          },
        ]);
      }

      // Check if there are more codes in the LIFO stack
      if (stackSnapshot && stackSnapshot.length > 0) {
        // Pop last code and start a new sub-flow
        const lastCode = stackSnapshot[stackSnapshot.length - 1];
        const remainingStack = stackSnapshot.slice(0, stackSnapshot.length - 1);
        setQuestionCodeStack(remainingStack);
        setCurrentQuestion(null);
        resetFormState();
        setCompletedCodes((prev) => prev + 1);
        await startFlowWithCode(lastCode, remainingStack);
      } else {
        // All flows complete — go to Terms
        setCurrentQuestion(null);
        setCurrentPhase(PHASES.TERMS);
        setIsEndQuestion(true);
      }
    }
  };

  // ─── Summary generation ─────────────────────────────────────────────────────

  const generateSummaryForAnswer = async (question, answerText, responseTo) => {
    if (!question || !answerText) return '';

    setLoadingQuestionId(question);
    setSummaryLoading(true);

    const placeholderSummary = {
      question,
      answer: '',
      isLoading: true,
      responseTo,
    };
    setProposal((prev) => [placeholderSummary, ...prev]);

    try {
      const payload = { question, responseData: answerText };
      const data = await getSummaryGenerator(payload);

      const summaryText = data?.output || 'Unable to generate summary.';
      const newSummary = { question, answer: summaryText, isLoading: false, responseTo };

      cacheSummary(question, summaryText, null, responseTo);
      setProposal((prev) => prev.map((item) => (item.question === question ? newSummary : item)));

      return summaryText;
    } catch (err) {
      console.error('Error generating summary:', err);
      const errorText = 'Error generating summary.';
      const errorSummary = { question, answer: errorText, isLoading: false, responseTo };
      cacheSummary(question, errorText, null, responseTo);
      setProposal((prev) => prev.map((item) => (item.question === question ? errorSummary : item)));
      return errorText;
    } finally {
      setSummaryLoading(false);
      setLoadingQuestionId(null);
    }
  };

  // ─── Completion ─────────────────────────────────────────────────────────────

  const completeProcedure = async () => {
    setLoading(true);

    const applicationPayload = {
      address: propertyAddress,
      description: description,
      fullName: fullName,
      procedureId: selectedProcedure?._id,
      overlayArray: overlays
        ?.filter(
          (overlayName) =>
            (overlayName?.overlayName || overlayName?.overlay)?.toLowerCase() !== 'title report'
        )
        .map((overlayName) => ({
          overlay: overlayName?.overlayName || overlayName?.overlay,
        })),
      phoneNumber: phone,
      email: email,
      propertyDetails: zoneDetails,
      assessmentAnswers: allAnswers,
      completionTimestamp: new Date().toISOString(),
      screenshotId: screenshotId || screenshotIdFromUrl,
      recordId: cacheOverlayScreenshots?.find((item) => item?.name === true)?.screenshot,
      ...(applicationIdForConvertToFinal && { applicationId: applicationIdForConvertToFinal }),
      folderName,
    };

    try {
      const applicationResponse = await saveApplication(applicationPayload);
      const applicationId = applicationResponse?.applicationId;

      if (!applicationId) {
        handleError('Failed to save the application.', 'application.save.error');
        return;
      }

      setItem('applicationId', applicationId);

      const chatResponsePayload = {
        applicationId: applicationId,
        responseArray: summarayCache,
      };

      await saveChatResponse(chatResponsePayload);
      cacheAnswer('TERMINATE', 'TERMINATE');
      setIsProcedureCompleted(true);
      setCurrentPhase(PHASES.COMPLETED);
    } catch (appError) {
      console.error('Error saving application:', appError);
      handleError('Failed to save application. Please try again.', 'application.save.retry.error');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = () => {
    completeProcedure();
  };

  const handleReportView = () => {
    setIsEndQuestion(false);
    clearFlow();
    navigate('/reviewAndLodge');
  };

  const handleStartNewProcedure = () => {
    clearCache();
    clearSummaryCache();
    clearItem('applicationId');
    clearFlow();
    navigate('/');
  };

  const handleNotSupportedClose = () => {
    setShowNotSupportedModal(false);
    handleStartNewProcedure();
  };

  const renderAnswerInput = () => {
    if (!currentQuestion) return null;

    const { questionType, options } = currentQuestion;

    if (questionType === 'OPTION') {
      return (
        <RadioGroup>
          {options.map((option) => (
            <RadioOption key={option} checked={answer === option}>
              <input
                type="radio"
                name="answer"
                value={option}
                checked={answer === option}
                onChange={(e) => setAnswer(e.target.value)}
                disabled={loading || summaryLoading}
              />
              <span>{option}</span>
            </RadioOption>
          ))}
        </RadioGroup>
      );
    }

    if (questionType === 'NUMBER') {
      return (
        <TextInput
          id="answer-input"
          type="number"
          min="0"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder={intl.formatMessage({ id: 'input.answer.placeholder' })}
          aria-labelledby="answer-label"
          aria-required="true"
          disabled={loading || summaryLoading}
        />
      );
    }

    // TEXT (default)
    return (
      <TextInput
        id="answer-input"
        type="text"
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        placeholder={intl.formatMessage({ id: 'input.answer.placeholder' })}
        aria-labelledby="answer-label"
        aria-required="true"
        disabled={loading || summaryLoading}
      />
    );
  };

  const renderCurrentQuestion = () => {
    if (!currentQuestion) return null;

    return (
      <>
        <QuestionBox role="heading" aria-level="1">
          {currentQuestion.clusterData?.name}
        </QuestionBox>

        <QuestionBox role="heading" aria-level="2">
          {currentQuestion.question}
        </QuestionBox>

        {validationError && (
          <ErrorMessageComponent message={validationError} role="alert" aria-live="assertive" />
        )}

        <div role="form">
          <label id="answer-label" htmlFor="answer-input" className="visually-hidden">
            <IntlMessages id="input.answer.label" />
          </label>
          {renderAnswerInput()}
        </div>

        <ContinueButton
          onClick={handleSubmit}
          disabled={!answer || loading || summaryLoading}
          aria-disabled={!answer || loading || summaryLoading}
        >
          {loading ? (
            <IntlMessages id="button.loading" defaultMessage="Loading..." />
          ) : (
            <IntlMessages id="button.next" defaultMessage="Next" />
          )}
        </ContinueButton>
      </>
    );
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <Main role="main">
      {showNotSupportedModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <AlertCircle size={32} color="#dc2626" />
              <ModalTitle>Not a Supported Development</ModalTitle>
            </ModalHeader>
            <ModalText>
              The use you&apos;ve proposed in {zoneDetails?.zoneName || 'Rural Residential'} would
              be classed as an Impact Assessable Application and it may be possible if assessed on
              its own merits and achieves the outcomes of the zone. <br />
              We would recommend you seek further advice from a town planner.
            </ModalText>
            <ModalButton onClick={handleNotSupportedClose}>Return to Home</ModalButton>
          </ModalContent>
        </ModalOverlay>
      )}

      <SectionLeft
        className="hide-scroll-bar"
        aria-label="Question Section"
        isEndQuestion={isEndQuestion}
      >
        {error ? (
          <ContentBox>
            <ErrorMessageComponent message={error} role="alert" aria-live="assertive" />
          </ContentBox>
        ) : (
          <>
            {isProcedureCompleted ? (
              <ContentBox>
                <QuestionBox role="heading" aria-level="1">
                  <PropertyLabel as="h2" style={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircle size={24} style={{ color: '#3DB654', marginRight: '8px' }} />
                    <IntlMessages id="preparation.completed.title" />
                  </PropertyLabel>
                </QuestionBox>
                <p style={{ fontSize: '14px', lineHeight: '22px' }}>
                  <FormattedMessage
                    id="preparation.completed.description"
                    values={{
                      procedureName: selectedProcedure?.procedureName,
                    }}
                  />{' '}
                  <ActionLink onClick={handleReportView} style={{ marginRight: '10px' }}>
                    <IntlMessages id="button.final.report" defaultMessage="Final Report" />
                  </ActionLink>{' '}
                  <ActionLink onClick={handleStartNewProcedure}>
                    <IntlMessages id="button.start.new" defaultMessage="Start New Procedure" />
                  </ActionLink>
                </p>
              </ContentBox>
            ) : (
              <ContentBox role="region" aria-label="Question Form" className="hide-scroll-bar">
                {loading ? (
                  <LoadingScreen />
                ) : isEndQuestion ? (
                  <TermsAndConditions handleGenerateReport={handleGenerateReport} />
                ) : (
                  renderCurrentQuestion()
                )}
              </ContentBox>
            )}
          </>
        )}

        <Wrapper>
          <ContentBoxRight>
            <section aria-label="Proposal Summary">
              <PropertyLabel as="h2">
                <IntlMessages id="proposal.title" />
              </PropertyLabel>
              <div style={{ display: 'grid', gap: '10px' }}>
                <PropertyValue style={{ fontWeight: 'bold' }}>
                  1. {selectedProcedure?.procedureName || 'Loading...'}
                </PropertyValue>
                <PropertyValue className="hide-scroll-bar">
                  <ul
                    aria-label="Proposal details"
                    style={{ marginLeft: '25px', listStyleType: 'disc' }}
                  >
                    {isProcedureCompleted ? (
                      <>
                        {summarayCache?.map((item, index) => (
                          <li key={index}>{item?.answer}</li>
                        ))}
                      </>
                    ) : proposal.length > 0 ? (
                      proposal?.map((item, index) => (
                        <li key={index}>
                          {item.isLoading ? (
                            <span role="status" aria-live="polite">
                              <IntlMessages id="proposal.status.generating" />
                            </span>
                          ) : (
                            item?.answer
                          )}
                        </li>
                      ))
                    ) : (
                      <IntlMessages id="proposal.status.pending" />
                    )}
                  </ul>
                </PropertyValue>
              </div>
            </section>
          </ContentBoxRight>

          {!isMobile && (
            <ContentBox aria-label="Property Details">
              <PropertyList role="list">
                <PropertyItem role="listitem">
                  <PropertyLabel as="h2">
                    <IntlMessages id="property.details.address" />
                  </PropertyLabel>
                  <PropertyValue>{propertyAddress}</PropertyValue>
                </PropertyItem>

                <PropertyItem role="listitem">
                  <PropertyLabel as="h2">
                    <IntlMessages id="property.details.size" />
                  </PropertyLabel>
                  <PropertyValue>
                    {`${zoneDetails.preLdzArea}m²` || <IntlMessages id="fetch.error" />}
                  </PropertyValue>
                </PropertyItem>
                <PropertyItem role="listitem">
                  <PropertyLabel as="h2">
                    <IntlMessages id="property.details.zone" />
                  </PropertyLabel>
                  <PropertyValue>
                    {zoneDetails?.zoneName || <IntlMessages id="fetch.error" />}
                  </PropertyValue>
                </PropertyItem>
              </PropertyList>
            </ContentBox>
          )}
        </Wrapper>
      </SectionLeft>

      <SectionRight>
        <MapWithOverlays searchAddress={propertyAddress} showDropdown={true} />
      </SectionRight>
    </Main>
  );
};

export default QuestionComponent;
